import React, { useEffect, useRef } from "react";

import { Button } from "src/primitives/Button";
import { ScrollArea } from "src/primitives/ScrollArea";
import { cn } from "src/shadcn/lib/utils";

import { COLUMN_HEIGHT, ITEM_HEIGHT } from "./constants";
import { padNumber } from "./utils";

interface TimeColumnProps {
  items: (number | string)[];
  selected: number | string;
  onSelect: (value: number | string) => void;
  disabled?: boolean;
  className?: string;
  padStart?: number;
}

const TimeColumn: React.FC<TimeColumnProps> = ({
  items,
  selected,
  onSelect,
  disabled = false,
  className,
  padStart = 2,
}) => {
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  }, [selected]);

  return (
    <ScrollArea
      className={cn("w-16", className)}
      style={{ height: COLUMN_HEIGHT }}
    >
      <div
        className="flex flex-col items-center py-2"
        role="listbox"
        aria-label="Time selection"
      >
        {items.map(item => {
          const isSelected = item === selected;
          const displayValue =
            typeof item === "number" ? padNumber(item, padStart) : item;

          return (
            <Button
              key={item}
              ref={isSelected ? selectedRef : undefined}
              variant={isSelected ? "default" : "ghost"}
              size="sm"
              role="option"
              aria-selected={isSelected}
              disabled={disabled}
              className={cn(
                "w-12 justify-center font-mono text-sm",
                !isSelected && "text-muted-foreground"
              )}
              style={{ height: ITEM_HEIGHT }}
              onClick={() => onSelect(item)}
            >
              {displayValue}
            </Button>
          );
        })}
      </div>
    </ScrollArea>
  );
};

TimeColumn.displayName = "TimeColumn";

export { TimeColumn };
